import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiDelete, operationsClient } from '@/lib/api';
import { estimateTemplateEndpoints } from '@/lib/api/endpoints';
import { estimateTemplateKeys } from '@/lib/queryKeys';

/**
 * Deletes an estimate template by ID via the operations microservice.
 */
export function useDeleteEstimateTemplate() {
  const queryClient = useQueryClient();

  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete<void>(operationsClient, estimateTemplateEndpoints.delete(id)),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: estimateTemplateKeys.lists() });
      // Scoped to inactive on purpose. The split view keeps the detail pane
      // mounted while this runs — the navigate away lives in the caller's
      // onSuccess and is async — and removing a query an observer still holds
      // does not end it: React Query rebuilds an empty one on the next render
      // and refetches it, so the pane re-read the template we had just deleted
      // and surfaced its 404 as a toast. Prefix matching meant the attachments
      // query 404'd the same way. Once the pane unmounts, gcTime collects the
      // entry anyway.
      queryClient.removeQueries({ queryKey: estimateTemplateKeys.detail(id), type: 'inactive' });
      toast.success('Estimate template deleted');
    },
  });
}
